import { tmsAllowRoles } from 'data/facts/userRolesValidations';
import { FactsScreenToastMessages } from 'data/toastMessages/facts';
import { useStore } from 'hooks/useStore';
import { targetMarketShareHistoryInterface } from 'Interfaces/FactsInterfaces/drugInterfaces';
import { futureTmsDeactivatationRequestPayload } from 'Interfaces/FactsInterfaces/tmsInterfaces';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { factScreenServices } from 'services/factsScreenServices';
import { deepCopy } from 'utils/deepCopy';
import { checkIfFunctionalityEnabled } from 'utils/facts/roleBasedFunctionalityAccess';

export const useMarketHistoryValues = (gfc: number, sellingCategory: string, cot: string) => {
  const {
    UserStore: { userModulesRoles },
  } = useStore();

  const [tmsStrategiesData, setTmsStrategiesData] = useState<targetMarketShareHistoryInterface>({
    past_tms_values: [],
    future_tms_values: [],
  });
  const [isTmsStrategiesDataLoading, setIsTmsStrategiesDataLoading] = useState(false);

  const [deletingIndexes, setDeletingIndexes] = useState<Set<number>>(new Set());
  const columnDetails = [
    {
      key: 'tms',
      label: 'TMS (%)',
    },
    {
      key: 'strategy',
      label: 'Strategy',
    },
    {
      key: 'startDate',
      label: 'Start Date',
    },
    {
      key: 'endDate',
      label: 'End Date',
    },
  ];

  const handleDeleteFutureTms = async (marketShareId: number) => {
    try {
      const tempDeletingIndexes = [...deletingIndexes];
      const tmepIndexes = new Set<number>([...tempDeletingIndexes]);
      tmepIndexes.add(marketShareId);
      setDeletingIndexes(tmepIndexes);

      // payload for deactivating future tms
      const payload: futureTmsDeactivatationRequestPayload = {
        MarketShareID: marketShareId,
      };

      // deactivate particular future tms value
      await factScreenServices.deactivateFutureTms(payload);

      // remove that particular markte share id future instance from already stored historical/future market share values data
      deleteParticularIndexFutureTms(marketShareId);

      // show success toast
      toast.success(FactsScreenToastMessages.TMS_HISTORY_DELETE_MESSAGE);
    } catch (_err) {
      toast.success("Future TMS couldn't be deleted, please try again later");
    } finally {
      const tempDeletingIndexes = [...deletingIndexes];
      const tmepIndexes = new Set<number>([...tempDeletingIndexes]);
      tmepIndexes.delete(marketShareId);
      setDeletingIndexes(tmepIndexes);
    }
  };

  const deleteParticularIndexFutureTms = (marketShareId: number) => {
    const tempTMSStrategiesData = deepCopy(tmsStrategiesData);
    tempTMSStrategiesData.future_tms_values = [];

    tmsStrategiesData.future_tms_values.forEach(data => {
      if (data.MarketShareID !== marketShareId) {
        tempTMSStrategiesData.future_tms_values.push(data);
      }
    });

    setTmsStrategiesData(tempTMSStrategiesData);
  };

  const checkIfCurrentRowIsBeingDelete = (marketShareId: number) => {
    if (deletingIndexes.has(marketShareId)) return true;
    return false;
  };

  const handleFetchTmsStrategiesData = async () => {
    try {
      setIsTmsStrategiesDataLoading(true);
      const data = await factScreenServices.getMarketShareHistoryValues({
        GFC: gfc.toString(),
        COT: cot,
        SellingCategory: sellingCategory,
      });
      setTmsStrategiesData(
        sellingCategory.toLowerCase() === 'direct' ? data.Direct : data.Indirect
      );
    } catch (_err) {
      toast.error('Could not fetch TMS strategies data');
    } finally {
      setIsTmsStrategiesDataLoading(false);
    }
  };

  useEffect(() => {
    handleFetchTmsStrategiesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial fetch only
  }, []);

  return {
    columnDetails,
    tmsStrategiesData,
    isTmsStrategiesDataLoading,
    handleDeleteFutureTms,
    checkIfCurrentRowIsBeingDelete,
    checkIfFunctionalityEnabled,
    userModulesRoles,
    tmsAllowRoles,
  };
};
